import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body1,
  Body2,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  ProgressBar,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { BrainSparkleRegular, Dismiss24Regular } from '@fluentui/react-icons';

import { ButtonLoading } from '@shared/components';

import {
  COMPLIANCE_CHECK_ANALYSIS_DURATION_MS,
  COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS,
  COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_OPTIONS,
  COMPLIANCE_CHECK_DEVICE_TYPE_LABELS,
  COMPLIANCE_CHECK_DEVICE_TYPE_OPTIONS,
  COMPLIANCE_CHECK_PROGRESS_STEPS,
  COMPLIANCE_CHECK_REGULATION_LABELS,
  COMPLIANCE_CHECK_REGULATION_OPTIONS,
  DEFAULT_COMPLIANCE_CHECK_CONFIG,
  createComplianceCheckResultUrl,
} from '../constants';
import { TComplianceCheckDeviceClassification, TComplianceCheckDeviceType, TComplianceCheckRegulation } from '../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '680px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXL,
  },
  introCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  fieldsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  progressCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  progressMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
  progressStep: {
    color: tokens.colorNeutralForeground3,
  },
  stepList: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground4,
  },
  activeStep: {
    color: tokens.colorNeutralForeground2,
  },
  completedStep: {
    color: tokens.colorBrandForeground1,
  },
  stepDot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStrokeAccessible,
    flexShrink: 0,
  },
  activeStepDot: {
    backgroundColor: tokens.colorBrandForeground1,
    boxShadow: `0 0 0 4px ${tokens.colorBrandBackground2}`,
  },
  completedStepDot: {
    backgroundColor: tokens.colorBrandForeground1,
  },
  closeButton: {
    marginLeft: tokens.spacingHorizontalS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
});

const PROGRESS_CHECKPOINTS = [
  { time: 0, value: 0 },
  { time: 0.08, value: 0.05 },
  { time: 0.18, value: 0.16 },
  { time: 0.27, value: 0.23 },
  { time: 0.39, value: 0.41 },
  { time: 0.51, value: 0.49 },
  { time: 0.63, value: 0.67 },
  { time: 0.74, value: 0.75 },
  { time: 0.86, value: 0.91 },
  { time: 0.93, value: 0.95 },
  { time: 1, value: 1 },
] as const;

const getInitialConfig = () => ({ ...DEFAULT_COMPLIANCE_CHECK_CONFIG });

const getBumpyProgress = (elapsedMs: number) => {
  const elapsedRatio = Math.min(elapsedMs / COMPLIANCE_CHECK_ANALYSIS_DURATION_MS, 1);

  for (let index = 1; index < PROGRESS_CHECKPOINTS.length; index += 1) {
    const previousCheckpoint = PROGRESS_CHECKPOINTS[index - 1];
    const nextCheckpoint = PROGRESS_CHECKPOINTS[index];

    if (elapsedRatio <= nextCheckpoint.time) {
      const segmentRatio =
        (elapsedRatio - previousCheckpoint.time) / (nextCheckpoint.time - previousCheckpoint.time || 1);

      return previousCheckpoint.value + (nextCheckpoint.value - previousCheckpoint.value) * segmentRatio;
    }
  }

  return 1;
};

type TProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  productId: string;
  submissionId: string;
};

const ComplianceCheckDialog = ({ open, setOpen, productId, submissionId }: TProps) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [regulation, setRegulation] = useState<TComplianceCheckRegulation>(DEFAULT_COMPLIANCE_CHECK_CONFIG.regulation);
  const [classification, setClassification] = useState<TComplianceCheckDeviceClassification>(
    DEFAULT_COMPLIANCE_CHECK_CONFIG.classification
  );
  const [deviceType, setDeviceType] = useState<TComplianceCheckDeviceType>(DEFAULT_COMPLIANCE_CHECK_CONFIG.deviceType);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const resetState = () => {
    const initialConfig = getInitialConfig();
    setRegulation(initialConfig.regulation);
    setClassification(initialConfig.classification);
    setDeviceType(initialConfig.deviceType);
    setProgress(0);
    setActiveStepIndex(0);
    setIsRunning(false);
  };

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  useEffect(() => {
    if (!isRunning) return undefined;

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const nextProgress = getBumpyProgress(elapsedMs);
      const nextStepIndex = Math.min(
        COMPLIANCE_CHECK_PROGRESS_STEPS.length - 1,
        Math.floor(nextProgress * COMPLIANCE_CHECK_PROGRESS_STEPS.length)
      );

      setProgress(nextProgress);
      setActiveStepIndex(nextStepIndex);

      if (nextProgress >= 1) {
        window.clearInterval(intervalId);
        setOpen(false);
        navigate(
          createComplianceCheckResultUrl({
            productId,
            submissionId,
            config: {
              regulation,
              classification,
              deviceType,
            },
          })
        );
      }
    }, 120);

    return () => window.clearInterval(intervalId);
  }, [classification, deviceType, isRunning, navigate, productId, regulation, setOpen, submissionId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isRunning) return;
    setOpen(nextOpen);
  };

  const currentStep = COMPLIANCE_CHECK_PROGRESS_STEPS[activeStepIndex];

  return (
    <Dialog open={open} onOpenChange={(_, data) => handleOpenChange(data.open)}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                className={classes.closeButton}
                disabled={isRunning}
                onClick={() => handleOpenChange(false)}
              />
            }
          >
            <div className={classes.titleRow}>
              <BrainSparkleRegular className={classes.titleIcon} />
              <span>Compliance check</span>
            </div>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            {!isRunning ? (
              <>
                <div className={classes.introCard}>
                  <div className="flex items-center justify-between gap-4">
                    <Body1>
                      Run an AI-based review for the current submission and surface potential compliance gaps.
                    </Body1>
                  </div>
                  <Caption1 className={classes.hint}>
                    No source documents are modified without explicit permission.
                  </Caption1>
                </div>

                <div className={classes.fieldsGrid}>
                  <Field label="Regulation / Standard" required>
                    <Dropdown
                      selectedOptions={[regulation]}
                      value={COMPLIANCE_CHECK_REGULATION_LABELS[regulation]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) setRegulation(data.optionValue as TComplianceCheckRegulation);
                      }}
                    >
                      {COMPLIANCE_CHECK_REGULATION_OPTIONS.map((option) => (
                        <Option key={option} value={option}>
                          {COMPLIANCE_CHECK_REGULATION_LABELS[option]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field label="Device Classification" required>
                    <Dropdown
                      selectedOptions={[classification]}
                      value={COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS[classification]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) {
                          setClassification(data.optionValue as TComplianceCheckDeviceClassification);
                        }
                      }}
                    >
                      {COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_OPTIONS.map((option) => (
                        <Option key={option} value={option}>
                          {COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS[option]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field label="Device Type" required>
                    <Dropdown
                      selectedOptions={[deviceType]}
                      value={COMPLIANCE_CHECK_DEVICE_TYPE_LABELS[deviceType]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue) setDeviceType(data.optionValue as TComplianceCheckDeviceType);
                      }}
                    >
                      {COMPLIANCE_CHECK_DEVICE_TYPE_OPTIONS.map((option) => (
                        <Option key={option} value={option}>
                          {COMPLIANCE_CHECK_DEVICE_TYPE_LABELS[option]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
              </>
            ) : (
              <div className={classes.progressCard}>
                <div className={classes.progressMeta}>
                  <Caption1>AI review in progress</Caption1>
                  <Caption1>{Math.round(progress * 100)}%</Caption1>
                </div>

                <ProgressBar value={progress} thickness="large" shape="rounded" />

                <Body2 className={classes.progressStep}>{currentStep}</Body2>

                <div className={classes.stepList}>
                  {COMPLIANCE_CHECK_PROGRESS_STEPS.map((step, index) => {
                    const isActive = index === activeStepIndex;
                    const isCompleted = index < activeStepIndex || progress >= 1;

                    return (
                      <div
                        key={step}
                        className={mergeClasses(
                          classes.stepRow,
                          isActive && classes.activeStep,
                          isCompleted && classes.completedStep
                        )}
                      >
                        <span
                          className={mergeClasses(
                            classes.stepDot,
                            isActive && classes.activeStepDot,
                            isCompleted && classes.completedStepDot
                          )}
                        />
                        <Caption1>{step}</Caption1>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {!isRunning ? (
              <>
                <ButtonLoading appearance="primary" onClick={() => setIsRunning(true)}>
                  Start
                </ButtonLoading>
                <Button appearance="secondary" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Caption1 className={classes.hint}>Redirecting automatically once the review is complete.</Caption1>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ComplianceCheckDialog;
